import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, AlertTriangle, Factory, Truck, Sliders,
  Package, CheckCircle2, Clock, RotateCcw, Search, X,
} from 'lucide-react';
import BatchGroupDetailModal from './BatchGroupDetailModal';
import DispatchBatchesModal from './DispatchBatchesModal';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const STATUS_BADGE = {
  'Planned':       'bg-zinc-800 text-zinc-300 border-zinc-700',
  'In Production': 'bg-orange-900/40 text-orange-200 border-orange-700',
  'Packing':       'bg-amber-900/40 text-amber-200 border-amber-700',
  'QC':            'bg-violet-900/40 text-violet-200 border-violet-700',
  'On Hold':       'bg-red-900/30 text-red-300 border-red-800',
  'Completed':     'bg-green-900/40 text-green-300 border-green-700',
};

/**
 * SalesOpsTable — Day-by-day Operations Table for one month.
 *
 *   Day+Date  →  Production (product, batches, size, output, RM lots, rework
 *                kg, loss %, target client)  →  Dispatch (invoices, clients,
 *                values).
 *
 *   * Loss % cells turn red when above the configurable threshold (default 5).
 *   * Empty days collapse by default; toggle "Show empty days" to see them.
 *   * Loss threshold is persisted to localStorage so it survives reloads.
 */
export default function SalesOpsTable() {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showEmpty, setShowEmpty] = useState(false);
  const [threshold, setThreshold] = useState(() => {
    const v = parseFloat(localStorage.getItem('opsTable.lossThreshold'));
    return Number.isFinite(v) && v > 0 ? v : 5;
  });
  const [drillGroup, setDrillGroup] = useState(null); // {group, dayDate}
  const [dispatchDrill, setDispatchDrill] = useState(null); // dispatch row
  const [search, setSearch] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  // Soft refresh: once data exists, keep the rendered table (and the user's scroll position) while re-fetching.
  const hasData = useRef(false);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(async () => {
    if (hasData.current) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(
        `${API}/sales/operations-table?year=${year}&month=${month}&loss_threshold_pct=${threshold}`,
        { withCredentials: true },
      );
      setData(res.data);
      hasData.current = true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load operations table');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [year, month, threshold]);
  const softReload = load;

  useEffect(() => { load(); }, [load]);

  const goPrev = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1);
  };
  const goNext = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1);
  };
  const updateThreshold = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n) || n < 0 || n > 100) return;
    setThreshold(n);
    localStorage.setItem('opsTable.lossThreshold', String(n));
  };

  const visibleDays = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const days = showEmpty ? data.days : data.days.filter(d => d.production.length || d.dispatch.length);
    if (!q) return days;
    const hit = (s) => (s || '').toLowerCase().includes(q);
    return days
      .map(d => ({
        ...d,
        production: d.production.filter(p => hit(p.product_name) || hit(p.for_client) || (p.batch_ids || []).some(hit)),
        dispatch: d.dispatch.filter(x => hit(x.client) || hit(x.invoice_number) || hit(x.items_summary) ||
          (x.items || []).some(it => hit(it.product_name) || (it.batches_dispatched || []).some(b => hit(b.batch_id)))),
      }))
      .filter(d => d.production.length || d.dispatch.length);
  }, [data, showEmpty, search]);

  const t = data?.month_totals || { batches_count: 0, output_kg: 0, dispatch_count: 0, dispatch_amount: 0, working_days: 0, alert_days: 0 };

  return (
    <div className="space-y-3" data-testid="sales-ops-table">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-zinc-900/60 border border-zinc-800 rounded-xl px-3 py-2">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} data-testid="ops-prev-month" className="p-1.5 rounded-md text-zinc-400 hover:text-amber-400 hover:bg-zinc-800">
            <ChevronLeft size={16} />
          </button>
          <div className="font-heading text-base font-semibold text-zinc-50 min-w-[160px] text-center" data-testid="ops-month-label">
            {MONTHS[month - 1]} {year}{refreshing && <span className="ml-2 text-[10px] text-zinc-500 font-normal animate-pulse" data-testid="ops-refreshing">updating…</span>}
          </div>
          <button onClick={goNext} data-testid="ops-next-month" className="p-1.5 rounded-md text-zinc-400 hover:text-amber-400 hover:bg-zinc-800">
            <ChevronRight size={16} />
          </button>
          <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); }}
            className="ml-1 text-[10px] px-2 py-1 rounded-md text-zinc-400 hover:text-amber-400 border border-zinc-800 hover:border-amber-500/50"
            data-testid="ops-today" title="Jump to current month">
            <RotateCcw size={9} className="inline mr-1" />This month
          </button>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="relative flex items-center" title="Search product, client, invoice or batch no">
            <Search size={12} className="absolute left-2 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product / client / batch…"
              data-testid="ops-search"
              className="w-56 bg-zinc-800 border border-zinc-700 rounded-md pl-6 pr-6 py-1 text-zinc-100 text-[11px] focus:outline-none focus:border-amber-500" />
            {search && <button onClick={() => setSearch('')} className="absolute right-1.5 text-zinc-500 hover:text-zinc-200" data-testid="ops-search-clear"><X size={11} /></button>}
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-400" title="Highlight rows with loss above this percentage">
            <Sliders size={11} className="text-red-400" />
            Loss red &gt;
            <input
              type="number" min="0" max="100" step="any"
              value={threshold}
              onChange={(e) => updateThreshold(e.target.value)}
              data-testid="ops-loss-threshold"
              className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-100 text-[11px] focus:outline-none focus:border-red-500"
            />
            <span className="text-zinc-500">%</span>
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)}
              data-testid="ops-show-empty" className="accent-amber-500" />
            Show empty days
          </label>
        </div>
      </div>

      {/* Month summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        <SummaryCard icon={<Factory size={13} className="text-orange-400"/>} label="Batches" value={t.batches_count} testid="ops-sum-batches" />
        <SummaryCard icon={<Package size={13} className="text-emerald-400"/>} label="Output (kg)" value={t.output_kg.toLocaleString('en-IN')} testid="ops-sum-output" />
        <SummaryCard icon={<Truck size={13} className="text-green-400"/>} label="Dispatches" value={t.dispatch_count} testid="ops-sum-dispatches" />
        <SummaryCard icon={<Truck size={13} className="text-amber-400"/>} label="Dispatch ₹" value={fmt(t.dispatch_amount)} testid="ops-sum-amount" />
        <SummaryCard icon={<CheckCircle2 size={13} className="text-blue-400"/>} label="Working Days" value={t.working_days} testid="ops-sum-working" />
        <SummaryCard icon={<AlertTriangle size={13} className="text-red-400"/>} label="Alert Days" value={t.alert_days} testid="ops-sum-alerts" hot={t.alert_days > 0} />
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        {loading && !data ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading operations table…</div>
        ) : visibleDays.length === 0 ? (
          <div className="p-10 text-center">
            <Clock className="mx-auto text-zinc-700 mb-2" size={28} />
            <div className="text-zinc-300 text-sm font-medium">{search ? `Nothing matches “${search}” in ${MONTHS[month - 1]} ${year}` : `No production or dispatches in ${MONTHS[month - 1]} ${year}`}</div>
            <div className="text-zinc-500 text-xs mt-1">{search ? 'Try another product or clear the search.' : 'Toggle “Show empty days” to see the full month grid.'}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950/80 border-b border-zinc-800 sticky top-0 z-10">
                <tr>
                  <th className="text-left text-zinc-400 font-medium px-3 py-2.5 w-24 whitespace-nowrap">Day · Date</th>
                  <th className="text-left text-zinc-400 font-medium px-3 py-2.5">
                    <span className="inline-flex items-center gap-1"><Factory size={11} className="text-orange-400"/> Production Info</span>
                  </th>
                  <th className="text-left text-zinc-400 font-medium px-3 py-2.5 w-72">
                    <span className="inline-flex items-center gap-1"><Truck size={11} className="text-green-400"/> Dispatch</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleDays.map(day => {
                  const isWeekend = day.is_weekend;
                  const hasAlert = day.production.some(p => p.loss_alert);
                  return (
                    <tr key={day.date}
                      className={`border-b border-zinc-800/40 align-top ${isWeekend ? 'bg-zinc-950/30' : ''} ${hasAlert ? 'bg-red-950/10' : ''}`}
                      data-testid={`ops-row-${day.date}`}>
                      {/* Day + Date */}
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className={`text-zinc-100 font-semibold ${hasAlert ? 'text-red-200' : ''}`}>{day.day_of_week}</div>
                        <div className="text-zinc-500 font-mono text-[11px]">{day.date.slice(8, 10)}/{day.date.slice(5, 7)}</div>
                        {hasAlert && (
                          <div className="mt-1 inline-flex items-center gap-0.5 text-[9px] text-red-400 font-medium">
                            <AlertTriangle size={9}/> Loss
                          </div>
                        )}
                      </td>

                      {/* Production cell */}
                      <td className="px-3 py-2.5">
                        {day.production.length === 0 ? (
                          <span className="text-zinc-700 italic text-[11px]">—</span>
                        ) : (
                          <div className="space-y-2">
                            {day.production.map((p, i) => (
                              <ProductionRow
                                key={i}
                                p={p}
                                threshold={threshold}
                                testid={`ops-prod-${day.date}-${i}`}
                                onClick={() => setDrillGroup({ group: p, dayDate: day.date })}
                                onAfterChange={softReload}
                              />
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Dispatch cell */}
                      <td className="px-3 py-2.5">
                        {day.dispatch.length === 0 ? (
                          <span className="text-zinc-700 italic text-[11px]">—</span>
                        ) : (
                          <div className="space-y-1.5">
                            {day.dispatch.map((d, i) => (
                              <DispatchRow key={i} d={d} testid={`ops-disp-${day.date}-${i}`} onClick={() => setDispatchDrill(d)} />
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Batch group drill-down modal (iter187 — click any production row to view + edit all sibling batches) */}
      {drillGroup && (
        <BatchGroupDetailModal
          group={drillGroup.group}
          dayDate={drillGroup.dayDate}
          onClose={() => { setDrillGroup(null); softReload(); }}
          isAdmin={isAdmin}
        />
      )}
      {dispatchDrill && (
        <DispatchBatchesModal d={dispatchDrill} onClose={() => setDispatchDrill(null)} onSaved={softReload} />
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, testid, hot }) {
  return (
    <div className={`bg-zinc-900/60 border rounded-lg px-3 py-2 ${hot ? 'border-red-700/60' : 'border-zinc-800'}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">{icon}{label}</div>
      <div className={`text-base font-bold mt-0.5 ${hot ? 'text-red-300' : 'text-zinc-100'}`}>{value}</div>
    </div>
  );
}

function ProductionRow({ p, threshold, testid, onClick, onAfterChange }) {
  const effThreshold = (p.loss_threshold_pct != null) ? p.loss_threshold_pct : threshold;
  const lossRed = p.loss_alert || p.loss_pct > effThreshold;
  const statusCls = STATUS_BADGE[p.status] || STATUS_BADGE['Planned'];
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [thresholdInput, setThresholdInput] = useState(String(effThreshold));
  const hasActual = (p.output_kg_actual || 0) > 0;
  // iter193 — Use the expected-for-completed-batches number (which honours
  // expected_output_override + rework) so the row matches the Batch Details
  // modal exactly. Planned-only used to drop rework and confused operators.
  const expectedKg = hasActual
    ? (p.expected_for_completed || p.expected_with_rework || p.output_kg_planned || 0)
    : (p.expected_with_rework || p.output_kg_planned || 0);
  const actualKg = p.output_kg_actual || 0;

  const saveThreshold = async (e) => {
    e?.stopPropagation();
    const v = parseFloat(thresholdInput);
    if (!Number.isFinite(v) || v < 0 || v > 100) {
      toast.error('Threshold must be 0-100%');
      return;
    }
    if (!p.product_id) {
      toast.error('Cannot set threshold — product not linked');
      return;
    }
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/inventory/finished-goods/${p.product_id}`,
        { loss_threshold_pct: v },
        { withCredentials: true },
      );
      toast.success(`Threshold set to ${v}% for ${p.product_name}`);
      setEditingThreshold(false);
      // iter193 — Trigger parent reload via callback prop (was a custom
      // event before; the strict React 19 hooks rule disliked calling
      // setState transitively from useEffect-installed handlers).
      if (onAfterChange) onAfterChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save threshold');
    }
  };

  return (
    <div
      className={`rounded-md border px-2 py-1.5 cursor-pointer hover:ring-1 hover:ring-amber-500/40 transition-shadow ${lossRed ? 'bg-red-950/30 border-red-800/50' : 'bg-zinc-950/60 border-zinc-800'}`}
      data-testid={testid}
      onClick={onClick}
      role="button"
      title="Click to view all batches in this group · ingredients · edit output/loss"
    >
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="text-zinc-100 font-semibold text-[12px] truncate max-w-[220px]">{p.product_name || '—'}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded border bg-amber-900/30 text-amber-300 border-amber-700/40 font-mono">
          {p.batches_count}× {p.batch_size_kg || '?'}kg
        </span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${statusCls}`}>{p.status}</span>
        {p.line_code && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700 font-mono">{p.line_code}</span>}
      </div>

      {/* iter185 — Show batch IDs explicitly */}
      {Array.isArray(p.batch_ids) && p.batch_ids.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mb-1" data-testid={`${testid}-batch-ids`}>
          <span className="text-[9px] text-zinc-500">Batches:</span>
          {p.batch_ids.slice(0, 5).map(bid => (
            <span key={bid} className="text-[9px] font-mono bg-sky-900/30 text-sky-300 border border-sky-700/40 px-1 py-0.5 rounded">{bid}</span>
          ))}
          {p.batch_ids.length > 5 && <span className="text-[9px] text-zinc-500">+{p.batch_ids.length - 5}</span>}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap text-[10px] text-zinc-400">
        <span><span className="text-zinc-500">For</span> <span className="text-sky-300 font-medium">{p.for_client || 'General Stock'}</span></span>

        {/* iter190/193 — Expected · Got · Loss layout per operator request.
            Expected = BOM planned + rework added back (and honours operator
            override on a per-batch basis). Loss is only computed against
            completed batches so partials don't show a fake 80% loss. */}
        {hasActual ? (
          <>
            <span title="Planned BOM output + rework added">
              <span className="text-zinc-500">Expected</span>{' '}
              <span className="text-amber-300 font-mono">{expectedKg} kg</span>
              {p.rework_added_kg > 0 && (
                <span className="text-zinc-600 ml-1 text-[9px]">
                  (plan {p.output_kg_planned} + rework {p.rework_added_kg})
                </span>
              )}
            </span>
            <span>
              <span className="text-zinc-500">Got</span>{' '}
              <span className="text-emerald-300 font-mono">{actualKg} kg</span>
            </span>
          </>
        ) : (
          <span>
            <span className="text-zinc-500">Output</span>{' '}
            <span className="text-emerald-300 font-mono">{p.output_kg} kg</span>
          </span>
        )}

        <span title={`Threshold ${effThreshold}% · ${p.has_product_threshold ? 'per-product' : 'global default'}`} data-testid={`${testid}-loss`}>
          <span className="text-zinc-500">Loss</span>{' '}
          <span className={`font-mono font-semibold ${lossRed ? 'text-red-300' : 'text-zinc-200'}`}>
            {p.loss_pct}%{lossRed && <AlertTriangle size={9} className="inline ml-0.5" />}
          </span>
          {p.loss_kg > 0 && (
            <span className="text-zinc-600 ml-1 text-[9px]">({p.loss_kg} kg)</span>
          )}
          {/* iter190 — inline per-product threshold setter */}
          {p.product_id && (
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); setEditingThreshold(v => !v); }}
              className="ml-1.5 px-1 py-0.5 rounded text-[8px] font-medium border border-zinc-700 bg-zinc-900 text-zinc-400 hover:text-amber-300 hover:border-amber-700/50 transition-colors inline-flex items-center gap-0.5"
              data-testid={`${testid}-set-threshold`}
              title="Set acceptable loss % threshold for this product"
            >
              <Sliders size={8}/> {effThreshold}%
            </span>
          )}
        </span>
        {p.rework_used_kg > 0 && (
          <span title="Old / rework material added back into this run">
            <span className="text-zinc-500">Old/Rework</span>{' '}
            <span className="text-amber-300 font-mono">{p.rework_used_kg} kg</span>
          </span>
        )}
      </div>

      {/* iter190 — Inline threshold editor */}
      {editingThreshold && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-1.5 px-2 py-1.5 bg-zinc-900 border border-amber-700/40 rounded-md flex items-center gap-2"
          data-testid={`${testid}-threshold-editor`}
        >
          <span className="text-[10px] text-zinc-400">Acceptable loss for <span className="text-amber-300 font-medium">{p.product_name}</span>:</span>
          <input
            type="number"
            min="0"
            max="100"
            step="any"
            value={thresholdInput}
            onChange={e => setThresholdInput(e.target.value)}
            onClick={e => e.stopPropagation()}
            className="w-16 px-1.5 py-0.5 text-[11px] font-mono bg-zinc-950 border border-zinc-700 rounded text-zinc-100 focus:outline-none focus:border-amber-500"
            data-testid={`${testid}-threshold-input`}
            autoFocus
          />
          <span className="text-[10px] text-zinc-500">%</span>
          <button
            onClick={saveThreshold}
            className="px-2 py-0.5 text-[10px] bg-amber-700 hover:bg-amber-600 text-zinc-50 rounded font-medium"
            data-testid={`${testid}-threshold-save`}
          >Save</button>
          <button
            onClick={(e) => { e.stopPropagation(); setEditingThreshold(false); }}
            className="px-2 py-0.5 text-[10px] bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded"
          >Cancel</button>
        </div>
      )}

      {p.rm_lots.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mt-1">
          <span className="text-[9px] text-zinc-500">RM Lots:</span>
          {p.rm_lots.slice(0, 6).map(l => (
            <span key={l} className="text-[9px] font-mono bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1 py-0.5 rounded" data-testid={`${testid}-lot-${l}`}>{l}</span>
          ))}
          {p.rm_lots.length > 6 && <span className="text-[9px] text-zinc-500">+{p.rm_lots.length - 6}</span>}
        </div>
      )}

      {p.batch_numbers.length > 0 && (
        <div className="text-[9px] text-zinc-600 mt-0.5 font-mono truncate" title={p.batch_numbers.join(', ')}>
          {p.batch_numbers.slice(0, 3).join(' · ')}{p.batch_numbers.length > 3 ? ` +${p.batch_numbers.length - 3}` : ''}
        </div>
      )}
    </div>
  );
}

function DispatchRow({ d, testid, onClick }) {
  const payCls = d.payment_status === 'Paid'
    ? 'text-green-300 bg-green-900/30 border-green-700/40'
    : d.payment_status === 'Partial'
      ? 'text-amber-300 bg-amber-900/30 border-amber-700/40'
      : 'text-red-300 bg-red-900/30 border-red-700/40';
  const batches = (d.items || []).flatMap(it => it.batches_dispatched || []);
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 cursor-pointer hover:ring-1 hover:ring-green-500/40 transition-shadow"
      data-testid={testid} onClick={onClick} role="button" title="Click for GST, transport and batch-wise dispatch">
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="font-mono text-[10px] text-amber-300">{d.invoice_number}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${payCls}`}>{d.payment_status}</span>
      </div>
      <div className="text-[11px] text-zinc-100 truncate font-medium">{d.client}</div>
      <div className="text-[10px] text-zinc-500 truncate">{d.items_summary}</div>
      <div className="flex items-center gap-2 flex-wrap mt-0.5">
        <span className="text-[10px] text-emerald-400 font-mono font-semibold">{fmt(d.total_amount)}</span>
        <span className="text-[9px] text-sky-300 font-mono" data-testid={`${testid}-gst`}>{d.gst_enabled ? `GST ${fmt(d.gst_amount)}` : 'Non-GST'}</span>
        {d.lr_no && <span className="text-[9px] text-zinc-500 font-mono">LR {d.lr_no}</span>}
      </div>
      {batches.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap mt-1" data-testid={`${testid}-batches`}>
          <span className="text-[9px] text-zinc-500">Batches:</span>
          {batches.slice(0, 4).map((b, i) => (
            <span key={i} className="text-[9px] font-mono bg-sky-900/30 text-sky-300 border border-sky-700/40 px-1 py-0.5 rounded">{b.batch_id} · {b.quantity}</span>
          ))}
          {batches.length > 4 && <span className="text-[9px] text-zinc-500">+{batches.length - 4}</span>}
        </div>
      )}
    </div>
  );
}
